import { Col, Divider, Row } from 'antd';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import config from 'config';
import {
  DonationOutput,
  DonorOutput,
  CollectionSet,
  PermissionsEnum,
} from 'gql/types';
import { ControlledDatePicker, FormItem } from 'modules/common/components';
import {
  DonorInfoSection,
  ExaminationSection,
  EnvironmentSection,
  DonationInfoSection,
  QuantitySection,
} from 'modules/donations/components';
import { DonorAvatar } from 'modules/donor/components';
import { useDonorAvatarUrl } from 'modules/donor/hooks/useDonorAvatarUrl';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = {
  donor: DonorOutput | undefined;
  previousDonation: DonationOutput | undefined;
  sectionNamePrefix: string;
  formEnabled: boolean;
};

const VitalsSection: FC<Props> = ({
  donor,
  previousDonation,
  sectionNamePrefix,
}: Props) => {
  const { t } = useTranslation('donation.VitalsSection');
  const breakpoint = useBreakpoint();
  const currentUser = useCurrentUser();
  const canViewDonorsPictures = currentUser.isCan(
    PermissionsEnum.ViewDonorsPictures
  );

  const { control, getFieldState, formState, watch, setValue, resetField } =
    useFormContext();

  const avatarUrl = useDonorAvatarUrl(donor?.pictures) || '';
  const weight = watch('vitalsSection.examination.weight');

  const autofillFieldsDueToWeight = (weight: number) => {
    const maxLimitValue = (
      ((weight * config.DONOR_CRITERIA.BLOOD_VOLUME_PER_KG) / 100) *
      config.DONOR_CRITERIA.BLOOD_VOLUME_PERCENTAGE
    ).toFixed(2);
    setValue('vitalsSection.donationInfo.maxLimit', maxLimitValue);

    const collectionSet =
      weight >= config.DONOR_CRITERIA.MIN_BIG_DONOR_WEIGHT
        ? CollectionSet.Quad
        : CollectionSet.Single;
    setValue('vitalsSection.donationInfo.collectionSet', collectionSet);
  };

  useEffect(() => {
    if (typeof weight !== 'number' || isNaN(Number(weight))) {
      resetField('vitalsSection.donationInfo.maxLimit');
      resetField('vitalsSection.donationInfo.collectionSet');
    } else {
      autofillFieldsDueToWeight(weight);
    }
  }, [weight]);

  const { error: dateError } = getFieldState(
    'vitalsSection.donationDate',
    formState
  );

  return (
    <>
      {breakpoint.xl && (
        <>
          <Row gutter={16}>
            <Divider>{t('vitalsSection')}</Divider>

            <Divider plain>{t('examination')}</Divider>
            <Col span={6}>
              <Row gutter={[0, 15]}>
                {canViewDonorsPictures && (
                  <Col span={24}>
                    <DonorAvatar src={avatarUrl} />
                  </Col>
                )}
                <Col span={24}>
                  <DonorInfoSection
                    enabled={false}
                    fieldNamePrefix="donor"
                    sectionNamePrefix={sectionNamePrefix}
                  />
                </Col>
                <Col span={24}>
                  <FormItem
                    required
                    label={t('donationDate')}
                    validateStatus={dateError ? 'error' : undefined}
                    extra={dateError?.message}
                  >
                    <ControlledDatePicker
                      placeholder={t('pickDonationDate')}
                      controlled={{
                        control: control,
                        name: 'vitalsSection.donationDate',
                      }}
                    />
                  </FormItem>
                </Col>
              </Row>
            </Col>
            <Col span={18}>
              <ExaminationSection
                enabled
                fieldNamePrefix="examination"
                sectionNamePrefix={sectionNamePrefix}
                previousDonation={previousDonation}
                showLinkToPreviousDonation
              />
            </Col>
          </Row>
          <Divider plain>{t('donationInfo')}</Divider>
          <Row gutter={16}>
            <Col span={6}>
              <EnvironmentSection
                enabled
                fieldNamePrefix="environment"
                sectionNamePrefix={sectionNamePrefix}
              />
            </Col>
            <Col span={12}>
              <DonationInfoSection
                enabled
                fieldNamePrefix="donationInfo"
                sectionNamePrefix={sectionNamePrefix}
              />
            </Col>
            <Col span={6}>
              <QuantitySection
                enabled
                fieldNamePrefix="quantity"
                sectionNamePrefix={sectionNamePrefix}
              />
            </Col>
          </Row>
        </>
      )}
      {!breakpoint.xl && (
        <Row gutter={[16, 16]}>
          {canViewDonorsPictures && (
            <Col span={24}>
              <Row>
                <Col>
                  <DonorAvatar src={avatarUrl} />
                </Col>
              </Row>
            </Col>
          )}
          <Col span={24}>
            <DonorInfoSection
              enabled={false}
              fieldNamePrefix="donor"
              sectionNamePrefix={sectionNamePrefix}
            />
          </Col>
          <Col span={24}>
            <FormItem
              required
              label={t('donationDate')}
              validateStatus={dateError ? 'error' : undefined}
              extra={dateError?.message}
            >
              <ControlledDatePicker
                placeholder={t('pickDonationDate')}
                controlled={{
                  control: control,
                  name: 'vitalsSection.donationDate',
                }}
              />
            </FormItem>
          </Col>
          <Col span={24}>
            <EnvironmentSection
              enabled
              fieldNamePrefix="environment"
              sectionNamePrefix={sectionNamePrefix}
            />
          </Col>
          <Col span={24}>
            <Divider plain orientation="left">
              {t('examination')}
            </Divider>
            <ExaminationSection
              fieldNamePrefix="examination"
              sectionNamePrefix={sectionNamePrefix}
              enabled
              previousDonation={previousDonation}
              showLinkToPreviousDonation
            />
          </Col>
          <Col span={24}>
            <Divider plain orientation="left">
              {t('donationInfo')}
            </Divider>
            <DonationInfoSection
              enabled
              fieldNamePrefix="donationInfo"
              sectionNamePrefix={sectionNamePrefix}
            />
          </Col>
          <Col span={24}>
            <QuantitySection
              enabled
              fieldNamePrefix="quantity"
              sectionNamePrefix={sectionNamePrefix}
            />
          </Col>
        </Row>
      )}
    </>
  );
};

export default VitalsSection;
